/**
 * Society events — community gatherings (Diwali, Holi, sports day, blood
 * donation camps, etc). NOT the same as Meeting (AGM/EGM governance which
 * lives under /governance).
 *
 * Two view modes:
 *  - Calendar (default): month grid with event chips per day, color-coded by
 *    category. Click chip → side drawer with detail.
 *  - List: filters + table; click row → drawer.
 *
 * Create wizard is a single Dialog with collapsible sections (basic / when /
 * where / audience / rsvp).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CalendarHeart,
  Plus,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { TabsPills } from '@/components/ui/tabs-pills';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ─── domain types ──────────────────────────────────────────────────
type EventStatus = 'draft' | 'published' | 'cancelled' | 'completed';
type EventCategory =
  | 'festival'
  | 'cultural'
  | 'sports'
  | 'community_service'
  | 'educational'
  | 'meeting'
  | 'birthday_anniversary'
  | 'other';
type EventAudience = 'all' | 'owners_only' | 'tenants_only' | 'block' | 'unit';

interface SocietyEventRow {
  id: string;
  title: string;
  description: string | null;
  category: EventCategory;
  audience: EventAudience;
  audience_block_id: string | null;
  audience_unit_id: string | null;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  location: string | null;
  amenity_id: string | null;
  status: EventStatus;
  recurrence_rule: string | null;
  cover_image_storage_key: string | null;
  external_link: string | null;
  rsvp_enabled: boolean;
  rsvp_deadline: string | null;
  rsvp_max_attendees: number | null;
  reminder_day_offsets: string | null;
  last_reminder_sent_at: string | null;
  created_at: string;
  amenity?: { id: string; name: string; kind: string } | null;
  _count?: { rsvps: number };
}

const CATEGORY_LABEL: Record<EventCategory, string> = {
  festival: 'Festival',
  cultural: 'Cultural',
  sports: 'Sports',
  community_service: 'Community service',
  educational: 'Educational',
  meeting: 'Meeting',
  birthday_anniversary: 'Birthday / Anniversary',
  other: 'Other'
};

// Tailwind palette classes per category — keep this stable so a chip in the
// calendar uses the same color as the badge in the side drawer.
const CATEGORY_COLOR: Record<EventCategory, { chip: string; dot: string }> = {
  festival: { chip: 'bg-amber-100 text-amber-900 border-amber-200', dot: 'bg-amber-500' },
  cultural: { chip: 'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200', dot: 'bg-fuchsia-500' },
  sports: { chip: 'bg-emerald-100 text-emerald-900 border-emerald-200', dot: 'bg-emerald-500' },
  community_service: { chip: 'bg-sky-100 text-sky-900 border-sky-200', dot: 'bg-sky-500' },
  educational: { chip: 'bg-indigo-100 text-indigo-900 border-indigo-200', dot: 'bg-indigo-500' },
  meeting: { chip: 'bg-slate-100 text-slate-900 border-slate-200', dot: 'bg-slate-500' },
  birthday_anniversary: { chip: 'bg-pink-100 text-pink-900 border-pink-200', dot: 'bg-pink-500' },
  other: { chip: 'bg-vt-surface-2 text-vt-text border-vt-border', dot: 'bg-vt-text-muted' }
};

const STATUS_VARIANT: Record<EventStatus, 'muted' | 'info' | 'warn' | 'ok' | 'alert'> = {
  draft: 'muted',
  published: 'info',
  cancelled: 'alert',
  completed: 'ok'
};

// ─── form schema ───────────────────────────────────────────────────
const CreateSchema = z
  .object({
    title: z.string().trim().min(2).max(160),
    description: z.string().trim().max(2000).optional().or(z.literal('')),
    category: z.enum([
      'festival',
      'cultural',
      'sports',
      'community_service',
      'educational',
      'meeting',
      'birthday_anniversary',
      'other'
    ]),
    starts_at: z.string().min(1, 'Required'),
    ends_at: z.string().optional().or(z.literal('')),
    all_day: z.boolean(),
    location: z.string().trim().max(200).optional().or(z.literal('')),
    audience: z.enum(['all', 'owners_only', 'tenants_only', 'block', 'unit']),
    rsvp_enabled: z.boolean(),
    rsvp_max_attendees: z
      .union([z.string().regex(/^\d+$/), z.literal('')])
      .optional(),
    rsvp_deadline: z.string().optional().or(z.literal('')),
    reminder_day_offsets: z.string().trim().max(60).optional().or(z.literal('')),
    external_link: z.string().trim().max(500).optional().or(z.literal('')),
    recurrence_rule: z.string().trim().max(160).optional().or(z.literal(''))
  })
  .refine((v) => !v.ends_at || v.ends_at >= v.starts_at, {
    message: 'End must be on or after start',
    path: ['ends_at']
  });

type CreateForm = z.infer<typeof CreateSchema>;

const DEFAULTS: CreateForm = {
  title: '',
  description: '',
  category: 'other',
  starts_at: '',
  ends_at: '',
  all_day: false,
  location: '',
  audience: 'all',
  rsvp_enabled: true,
  rsvp_max_attendees: '',
  rsvp_deadline: '',
  reminder_day_offsets: '-7,-1,0',
  external_link: '',
  recurrence_rule: ''
};

type ViewMode = 'calendar' | 'list';

// ─── calendar helpers ──────────────────────────────────────────────
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Build a 6×7 grid (42 cells) covering the calendar month — leading days from
 * previous month + trailing from next month, so the grid is always rectangular.
 * Week starts Monday (Indian society convention).
 */
function buildGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  // Monday=0..Sunday=6
  const dow = (first.getDay() + 6) % 7;
  const start = addDays(first, -dow);
  return Array.from({ length: 42 }).map((_, i) => addDays(start, i));
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

// ─── page ──────────────────────────────────────────────────────────
export function SocietyEventsPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>('calendar');
  const [createOpen, setCreateOpen] = useState(false);
  const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [drawerId, setDrawerId] = useState<string | null>(null);

  // List view filters
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<EventCategory | 'all'>('all');

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULTS,
    mode: 'onChange'
  });

  // Calendar fetch — span of grid (covers 42 days starting from Monday before
  // 1st of the visible month).
  const grid = useMemo(() => buildGrid(month), [month]);
  const from = grid[0]!;
  const to = addDays(grid[grid.length - 1]!, 1);

  const listQuery = useQuery({
    queryKey: ['society-events', view, ymdLocal(from), ymdLocal(to), statusFilter, categoryFilter],
    queryFn: () => {
      const params: Record<string, string> = {
        from: from.toISOString(),
        to: to.toISOString(),
        limit: '200'
      };
      if (view === 'list') {
        if (statusFilter !== 'all') params.status = statusFilter;
        if (categoryFilter !== 'all') params.category = categoryFilter;
      }
      return get<{ items: SocietyEventRow[]; next_cursor: string | null }>(
        '/society-events',
        { params }
      );
    }
  });

  const items = listQuery.data?.items ?? [];

  // Group events by day for calendar rendering.
  const byDay = useMemo(() => {
    const m = new Map<string, SocietyEventRow[]>();
    for (const e of items) {
      const day = ymdLocal(new Date(e.starts_at));
      const arr = m.get(day) ?? [];
      arr.push(e);
      m.set(day, arr);
    }
    // Sort within each day.
    for (const arr of m.values()) {
      arr.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
    }
    return m;
  }, [items]);

  const create = useMutation({
    mutationFn: (v: CreateForm) => {
      const body: Record<string, unknown> = {
        title: v.title.trim(),
        category: v.category,
        audience: v.audience,
        starts_at: new Date(v.starts_at).toISOString(),
        all_day: v.all_day,
        rsvp_enabled: v.rsvp_enabled
      };
      if (v.description?.trim()) body.description = v.description.trim();
      if (v.ends_at) body.ends_at = new Date(v.ends_at).toISOString();
      if (v.location?.trim()) body.location = v.location.trim();
      if (v.rsvp_max_attendees && v.rsvp_max_attendees !== '') {
        body.rsvp_max_attendees = Number(v.rsvp_max_attendees);
      }
      if (v.rsvp_deadline) body.rsvp_deadline = new Date(v.rsvp_deadline).toISOString();
      if (v.reminder_day_offsets?.trim()) body.reminder_day_offsets = v.reminder_day_offsets.trim();
      if (v.external_link?.trim()) body.external_link = v.external_link.trim();
      if (v.recurrence_rule?.trim()) body.recurrence_rule = v.recurrence_rule.trim();
      return post<SocietyEventRow>('/society-events', body);
    },
    onSuccess: () => {
      toast.success('Event created (draft)');
      setCreateOpen(false);
      form.reset(DEFAULTS);
      void qc.invalidateQueries({ queryKey: ['society-events'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Events"
        description="Community gatherings — Diwali, Holi, sports day, blood donation camps, society anniversaries. Residents RSVP and get reminders. Separate from AGM/governance meetings."
        actions={
          <div className="flex items-center gap-vt-2">
            <TabsPills
              size="sm"
              value={view}
              onChange={(v) => setView(v as ViewMode)}
              options={[
                { value: 'calendar', label: 'Calendar' },
                { value: 'list', label: 'List' }
              ]}
              aria-label="View mode"
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Create event
            </Button>
          </div>
        }
      />

      {view === 'calendar' ? (
        <Card>
          <CardContent className="p-vt-4">
            <div className="mb-vt-3 flex items-center justify-between">
              <div className="flex items-center gap-vt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-[170px] text-center text-vt-16 font-semibold text-vt-text">
                  {monthLabel(month)}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMonth(startOfMonth(new Date()))}
                >
                  Today
                </Button>
              </div>
              <div className="text-vt-12 text-vt-text-muted">
                {listQuery.isLoading ? 'Loading…' : `${items.length} event${items.length === 1 ? '' : 's'} in view`}
              </div>
            </div>

            <div className="overflow-hidden rounded-vt-md border border-vt-border">
              {/* Week headers */}
              <div className="grid grid-cols-7 bg-vt-surface-2 text-center text-vt-11 font-semibold uppercase tracking-wider text-vt-text-muted">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <div key={d} className="border-r border-vt-border py-vt-1 last:border-r-0">
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7 grid-rows-6">
                {grid.map((cell, idx) => {
                  const inMonth = cell.getMonth() === month.getMonth();
                  const isToday = ymdLocal(cell) === ymdLocal(new Date());
                  const dayEvents = byDay.get(ymdLocal(cell)) ?? [];
                  return (
                    <div
                      key={idx}
                      className={cn(
                        'min-h-[96px] border-b border-r border-vt-border p-vt-1 transition-colors',
                        idx % 7 === 6 && 'border-r-0',
                        idx >= 35 && 'border-b-0',
                        !inMonth && 'bg-vt-surface-2/40'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={cn(
                            'inline-flex h-5 min-w-[20px] items-center justify-center rounded-vt-pill text-vt-11 font-semibold',
                            isToday
                              ? 'bg-vt-primary text-white px-1.5'
                              : inMonth
                              ? 'text-vt-text'
                              : 'text-vt-text-soft'
                          )}
                        >
                          {cell.getDate()}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-col gap-[2px]">
                        {dayEvents.slice(0, 3).map((e) => {
                          const c = CATEGORY_COLOR[e.category];
                          return (
                            <button
                              key={e.id}
                              type="button"
                              onClick={() => setDrawerId(e.id)}
                              className={cn(
                                'w-full truncate rounded border px-1.5 py-0.5 text-left text-[10.5px] font-medium leading-tight transition-opacity hover:opacity-90',
                                c.chip,
                                e.status === 'cancelled' && 'line-through opacity-60'
                              )}
                              title={`${e.title} — ${formatDateTime(e.starts_at)}`}
                            >
                              {e.all_day
                                ? e.title
                                : `${new Date(e.starts_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ${e.title}`}
                            </button>
                          );
                        })}
                        {dayEvents.length > 3 ? (
                          <div className="px-1 text-[10px] text-vt-text-muted">
                            +{dayEvents.length - 3} more
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-vt-4">
            <div className="mb-vt-3 flex flex-wrap items-center gap-vt-2">
              <div className="flex items-center gap-vt-2">
                <Label className="text-vt-12 text-vt-text-muted">Status</Label>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as EventStatus | 'all')}
                  className="h-8 text-vt-13"
                >
                  <option value="all">All</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </Select>
              </div>
              <div className="flex items-center gap-vt-2">
                <Label className="text-vt-12 text-vt-text-muted">Category</Label>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value as EventCategory | 'all')}
                  className="h-8 text-vt-13"
                >
                  <option value="all">All</option>
                  {(Object.keys(CATEGORY_LABEL) as EventCategory[]).map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABEL[c]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {listQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                icon={<CalendarHeart className="h-6 w-6" />}
                title="No events in this window"
                description="Create a community event to kick things off."
                action={
                  <Button onClick={() => setCreateOpen(true)}>
                    <Plus className="h-4 w-4" /> Create event
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>RSVPs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((e) => {
                    const c = CATEGORY_COLOR[e.category];
                    return (
                      <TableRow
                        key={e.id}
                        className="cursor-pointer hover:bg-vt-surface-2/60"
                        onClick={() => setDrawerId(e.id)}
                      >
                        <TableCell className="font-medium text-vt-text">{e.title}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-vt-1">
                            <span className={cn('inline-block h-2 w-2 rounded-full', c.dot)} />
                            <span className="text-vt-13">{CATEGORY_LABEL[e.category]}</span>
                          </span>
                        </TableCell>
                        <TableCell className="text-vt-13">
                          {e.all_day ? formatDate(e.starts_at) : formatDateTime(e.starts_at)}
                        </TableCell>
                        <TableCell className="text-vt-13 capitalize">
                          {e.audience.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[e.status]}>{e.status}</Badge>
                        </TableCell>
                        <TableCell className="text-vt-13">{e._count?.rsvps ?? 0}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Drawer (detail) ─── */}
      {drawerId ? (
        <EventDetailDrawer
          id={drawerId}
          onClose={() => setDrawerId(null)}
          onOpenFullDetail={() => {
            const id = drawerId;
            setDrawerId(null);
            navigate({ to: '/society-events/$id', params: { id } }).catch(() => undefined);
          }}
        />
      ) : null}

      {/* ─── Create dialog ─── */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create event</DialogTitle>
            <DialogDescription>
              Saved as draft. Publish once details are confirmed.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-vt-3 md:grid-cols-2"
            onSubmit={form.handleSubmit((v) => create.mutate(v))}
          >
            <div className="md:col-span-2">
              <Label htmlFor="se-title">Title</Label>
              <Input id="se-title" {...form.register('title')} placeholder="Diwali night" />
              {form.formState.errors.title ? (
                <p className="mt-1 text-vt-11 text-vt-alert">{form.formState.errors.title.message}</p>
              ) : null}
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="se-desc">Description</Label>
              <Textarea
                id="se-desc"
                rows={3}
                {...form.register('description')}
                placeholder="What it's about, what to expect."
              />
            </div>
            <div>
              <Label htmlFor="se-cat">Category</Label>
              <Select id="se-cat" {...form.register('category')}>
                {(Object.keys(CATEGORY_LABEL) as EventCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="se-aud">Audience</Label>
              <Select id="se-aud" {...form.register('audience')}>
                <option value="all">Everyone</option>
                <option value="owners_only">Owners only</option>
                <option value="tenants_only">Tenants only</option>
                <option value="block">Specific tower / block</option>
                <option value="unit">Specific flat / unit</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="se-start">Starts at</Label>
              <Input id="se-start" type="datetime-local" {...form.register('starts_at')} />
              {form.formState.errors.starts_at ? (
                <p className="mt-1 text-vt-11 text-vt-alert">{form.formState.errors.starts_at.message}</p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="se-end">Ends at (optional)</Label>
              <Input id="se-end" type="datetime-local" {...form.register('ends_at')} />
              {form.formState.errors.ends_at ? (
                <p className="mt-1 text-vt-11 text-vt-alert">{form.formState.errors.ends_at.message}</p>
              ) : null}
            </div>
            <div className="md:col-span-2 flex items-center gap-vt-2">
              <input
                id="se-allday"
                type="checkbox"
                {...form.register('all_day')}
                className="h-4 w-4 rounded border-vt-border accent-vt-primary"
              />
              <Label htmlFor="se-allday" className="cursor-pointer text-vt-13">All-day event</Label>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="se-loc">Location</Label>
              <Input
                id="se-loc"
                {...form.register('location')}
                placeholder="Clubhouse / Block A garden / etc."
              />
            </div>
            <div>
              <Label htmlFor="se-link">External link (optional)</Label>
              <Input
                id="se-link"
                {...form.register('external_link')}
                placeholder="https://forms.gle/..."
              />
            </div>
            <div>
              <Label htmlFor="se-rec">Recurrence (RRULE, optional)</Label>
              <Input
                id="se-rec"
                {...form.register('recurrence_rule')}
                placeholder="FREQ=WEEKLY;COUNT=4"
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-vt-2">
              <input
                id="se-rsvp"
                type="checkbox"
                {...form.register('rsvp_enabled')}
                className="h-4 w-4 rounded border-vt-border accent-vt-primary"
              />
              <Label htmlFor="se-rsvp" className="cursor-pointer text-vt-13">Enable RSVP</Label>
            </div>
            <div>
              <Label htmlFor="se-cap">RSVP cap (optional)</Label>
              <Input
                id="se-cap"
                inputMode="numeric"
                {...form.register('rsvp_max_attendees')}
                placeholder="e.g. 150"
              />
            </div>
            <div>
              <Label htmlFor="se-dl">RSVP deadline (optional)</Label>
              <Input id="se-dl" type="datetime-local" {...form.register('rsvp_deadline')} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="se-rem">Reminder offsets (days from event)</Label>
              <Input id="se-rem" {...form.register('reminder_day_offsets')} placeholder="-7,-1,0" />
              <p className="mt-1 text-vt-11 text-vt-text-muted">
                CSV of integers. Negative = before. e.g. <code>-7,-1,0</code> = 7 days before, 1 day before, day of.
              </p>
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Side drawer (detail preview) ─────────────────────────────────
function EventDetailDrawer({
  id,
  onClose,
  onOpenFullDetail
}: {
  id: string;
  onClose: () => void;
  onOpenFullDetail: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['society-events', id],
    queryFn: () =>
      get<SocietyEventRow & { rsvp_summary?: { going: number; maybe: number; not_going: number; guests_total: number } }>(
        `/society-events/${id}`
      ),
    enabled: !!id
  });

  const publish = useMutation({
    mutationFn: () => post<SocietyEventRow>(`/society-events/${id}/publish`, {}),
    onSuccess: () => {
      toast.success('Event published');
      void qc.invalidateQueries({ queryKey: ['society-events'] });
      void qc.invalidateQueries({ queryKey: ['society-events', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const cancelM = useMutation({
    mutationFn: (reason: string) => post<SocietyEventRow>(`/society-events/${id}/cancel`, { reason }),
    onSuccess: () => {
      toast.success('Event cancelled');
      setCancelOpen(false);
      void qc.invalidateQueries({ queryKey: ['society-events'] });
      void qc.invalidateQueries({ queryKey: ['society-events', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const complete = useMutation({
    mutationFn: () => post<SocietyEventRow>(`/society-events/${id}/complete`, {}),
    onSuccess: () => {
      toast.success('Event marked completed');
      void qc.invalidateQueries({ queryKey: ['society-events'] });
      void qc.invalidateQueries({ queryKey: ['society-events', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const remove = useMutation({
    mutationFn: () => del(`/society-events/${id}`),
    onSuccess: () => {
      toast.success('Event deleted');
      setDeleteOpen(false);
      onClose();
      void qc.invalidateQueries({ queryKey: ['society-events'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const e = detail.data;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        role="presentation"
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-vt-surface shadow-vt-3"
        role="dialog"
        aria-label="Event detail"
      >
        <div className="flex items-center justify-between border-b border-vt-border px-vt-4 py-vt-3">
          <div className="min-w-0">
            <div className="text-vt-11 uppercase tracking-wide text-vt-text-muted">Event</div>
            <div className="truncate text-vt-16 font-semibold text-vt-text">
              {e?.title ?? 'Loading…'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-vt-sm text-vt-text-muted hover:bg-vt-surface-2 hover:text-vt-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-vt-4 py-vt-3 space-y-vt-3">
          {detail.isLoading || !e ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-vt-2">
                <Badge variant={STATUS_VARIANT[e.status]}>{e.status}</Badge>
                <span className={cn('inline-flex items-center gap-vt-1 rounded-vt-pill border px-2 py-0.5 text-vt-11', CATEGORY_COLOR[e.category].chip)}>
                  <span className={cn('h-2 w-2 rounded-full', CATEGORY_COLOR[e.category].dot)} />
                  {CATEGORY_LABEL[e.category]}
                </span>
              </div>
              <div>
                <div className="text-vt-11 uppercase tracking-wide text-vt-text-muted">When</div>
                <div className="mt-1 text-vt-14 text-vt-text">
                  {e.all_day ? formatDate(e.starts_at) : formatDateTime(e.starts_at)}
                  {e.ends_at ? <> &nbsp;–&nbsp; {e.all_day ? formatDate(e.ends_at) : formatDateTime(e.ends_at)}</> : null}
                </div>
                {e.recurrence_rule ? (
                  <div className="mt-1 text-vt-12 text-vt-text-muted">Repeats: {e.recurrence_rule}</div>
                ) : null}
              </div>
              {(e.location || e.amenity) ? (
                <div>
                  <div className="text-vt-11 uppercase tracking-wide text-vt-text-muted">Where</div>
                  <div className="mt-1 text-vt-14 text-vt-text">
                    {e.amenity?.name ?? e.location}
                  </div>
                </div>
              ) : null}
              {e.description ? (
                <div>
                  <div className="text-vt-11 uppercase tracking-wide text-vt-text-muted">About</div>
                  <p className="mt-1 whitespace-pre-line text-vt-14 text-vt-text">{e.description}</p>
                </div>
              ) : null}

              <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
                <div className="text-vt-11 uppercase tracking-wide text-vt-text-muted">RSVPs</div>
                <div className="mt-1 grid grid-cols-4 gap-vt-2 text-center">
                  <RsvpStat label="Going" value={e.rsvp_summary?.going ?? 0} />
                  <RsvpStat label="Maybe" value={e.rsvp_summary?.maybe ?? 0} />
                  <RsvpStat label="No" value={e.rsvp_summary?.not_going ?? 0} />
                  <RsvpStat label="Guests" value={e.rsvp_summary?.guests_total ?? 0} />
                </div>
              </div>

              {e.external_link ? (
                <div>
                  <div className="text-vt-11 uppercase tracking-wide text-vt-text-muted">Link</div>
                  <a
                    href={e.external_link}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block break-all text-vt-13 text-vt-primary underline"
                  >
                    {e.external_link}
                  </a>
                </div>
              ) : null}
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-vt-2 border-t border-vt-border px-vt-4 py-vt-3">
          <Button variant="ghost" size="sm" onClick={onOpenFullDetail}>
            Full detail
          </Button>
          {e?.status === 'draft' ? (
            <Button size="sm" onClick={() => publish.mutate()} disabled={publish.isPending}>
              Publish
            </Button>
          ) : null}
          {e?.status === 'published' ? (
            <Button size="sm" onClick={() => complete.mutate()} disabled={complete.isPending}>
              Mark completed
            </Button>
          ) : null}
          {e && e.status !== 'cancelled' && e.status !== 'completed' ? (
            <Button size="sm" variant="ghost" onClick={() => setCancelOpen(true)}>
              Cancel
            </Button>
          ) : null}
          {e?.status === 'draft' ? (
            <Button size="sm" variant="ghost" onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          ) : null}
        </div>
      </aside>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel this event?"
        description="Residents who RSVPed will see it as cancelled."
        confirmLabel="Cancel event"
        variant="warn"
        promptLabel="Reason (optional, shown in audit log)"
        promptPlaceholder="e.g. Rain forecast — moved to next weekend"
        onConfirm={(reason) => cancelM.mutate(reason ?? '')}
        loading={cancelM.isPending}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete draft event?"
        description="This permanently removes the draft. Cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
      />
    </>
  );
}

function RsvpStat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div>
      <div className="text-vt-18 font-semibold text-vt-text">{value}</div>
      <div className="text-vt-11 text-vt-text-muted">{label}</div>
    </div>
  );
}

// Suppress the unused import warning for `patch` — kept for parity with the
// rest of the routes module since detail-edit will land in a follow-up.
void patch;
